import { useState, useCallback, useRef } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

// British voice options for players (ElevenLabs)
export const PLAYER_VOICES = {
  alice: { name: 'Alice', description: 'Clear and friendly', gender: 'female' },
  george: { name: 'George', description: 'Warm storyteller', gender: 'male' },
  lily: { name: 'Lily', description: 'Confident and bright', gender: 'female' },
  daniel: { name: 'Daniel', description: 'Steady and clear', gender: 'male' },
};

// NPC voice mappings (all British)
export const NPC_VOICES = {
  farmer: { name: 'Farmer', voice: 'george' },
  baker: { name: 'Baker', voice: 'alice' },
  librarian: { name: 'Librarian', voice: 'daniel' },
  teacher: { name: 'Teacher', voice: 'alice' },
  gardener: { name: 'Gardener', voice: 'george' },
  shopkeeper: { name: 'Shopkeeper', voice: 'alice' },
  artist: { name: 'Artist', voice: 'lily' },
  scientist: { name: 'Scientist', voice: 'daniel' },
};

export const useTTS = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Stop any currently playing audio
  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsPlaying(false);
    setIsLoading(false);
  }, []);

  // Speak text with optional voice settings
  const speak = useCallback(async (text, options = {}) => {
    const { npcId, playerVoice, voice, speed, onEnd } = options;

    // Stop any current playback
    stop();

    if (!text || text.trim().length === 0) {
      return;
    }

    // Truncate text if too long (API limit is 4096 chars)
    const truncatedText = text.length > 4000 ? text.substring(0, 4000) + '...' : text;

    setIsLoading(true);
    setError(null);

    try {
      abortControllerRef.current = new AbortController();

      const response = await fetch(`${API}/api/tts/speak`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: truncatedText,
          npc_id: npcId,
          player_voice: playerVoice,
          voice: voice,
          speed: speed,
        }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error('Failed to generate speech');
      }

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);

      audioRef.current = new Audio(audioUrl);
      
      audioRef.current.onended = () => {
        setIsPlaying(false);
        URL.revokeObjectURL(audioUrl);
        if (onEnd) onEnd();
      };

      audioRef.current.onerror = () => {
        setIsPlaying(false);
        setError('Failed to play audio');
        URL.revokeObjectURL(audioUrl);
      };

      setIsLoading(false);
      setIsPlaying(true);
      await audioRef.current.play();

    } catch (err) {
      if (err.name === 'AbortError') {
        // Request was cancelled
        return;
      }
      console.error('TTS Error:', err);
      setError(err.message);
      setIsLoading(false);
    }
  }, [stop]);

  // Speak as a specific NPC
  const speakAsNPC = useCallback((text, npcId, options = {}) => {
    return speak(text, { ...options, npcId });
  }, [speak]);

  // Speak with player's chosen voice
  const speakAsPlayer = useCallback((text, playerVoice, options = {}) => {
    return speak(text, { ...options, playerVoice });
  }, [speak]);

  // Preview a voice
  const previewVoice = useCallback((voiceId, isNPC = false) => {
    const sampleTexts = [
      "Hello! Welcome to TypeTown!",
      "Let's practice typing together!",
      "Great job! You're doing amazing!",
      "Keep up the good work!",
    ];
    const randomText = sampleTexts[Math.floor(Math.random() * sampleTexts.length)];
    
    if (isNPC) {
      return speakAsNPC(randomText, voiceId);
    } else {
      return speakAsPlayer(randomText, voiceId);
    }
  }, [speakAsNPC, speakAsPlayer]);

  return {
    speak,
    speakAsNPC,
    speakAsPlayer,
    previewVoice,
    stop,
    isLoading,
    isPlaying,
    error,
  };
};

export default useTTS;
